import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import type { DiaryDocType, ProjectDocType } from '@ast-data/shared';
import { useStableQuery } from '../../db/useStableQuery';
import { formatDate } from '../../utils/formatDate';
import { projectMeasureNumber } from '../projects/projectTitle';

/**
 * "Tagebuch 26.09.2026" — and, for a day from a sibling Teilmaßnahme, its
 * Maßnahmennummer behind it: each phase keeps its own Tagebuch, so a family
 * can hold several days with the same date.
 */
export function useDiaryLabel(
    projectId: string,
    familyIds: string[]
): (diary: DiaryDocType) => string {
    const intl = useIntl();
    const projectsQuery = useStableQuery<ProjectDocType>({
        selector: { id: { $in: familyIds } }
    });
    const { results: projectDocs } = useLiveRxQuery<ProjectDocType>({
        collection: 'projects',
        query: projectsQuery
    });
    const measureNumbers = useMemo(
        () =>
            new Map(
                projectDocs.map((doc) => [
                    doc.id,
                    projectMeasureNumber(doc.toMutableJSON() as ProjectDocType)
                ])
            ),
        [projectDocs]
    );
    return useCallback(
        (diary: DiaryDocType) => {
            const label = intl.formatMessage(
                { id: 'diary.label' },
                { date: formatDate(diary.recordedAt) }
            );
            const measureNumber = measureNumbers.get(diary.projectId);
            if (diary.projectId === projectId || !measureNumber) {
                return label;
            }
            return `${label} (${measureNumber})`;
        },
        [intl, measureNumbers, projectId]
    );
}
